import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Lock, LogOut, Users, CalendarCheck, ChevronDown, ChevronUp } from "lucide-react";
import { Logo } from "../components/Logo";
import { Eyebrow } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ism_admin_token";

const STATUS_STYLES = {
  requested: "bg-gold/15 text-gold border-gold/40",
  confirmed: "bg-mist/15 text-mist border-mist/40",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const Detail = ({ label, value }) =>
  value ? (
    <div className="py-2 border-b border-line/60">
      <p className="font-mono text-[9px] tracking-[0.18em] uppercase text-inkmuted">{label}</p>
      <p className="mt-1 text-sm text-ink whitespace-pre-line">{Array.isArray(value) ? value.join(", ") : value}</p>
    </div>
  ) : null;

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [info, setInfo] = useState("");
  const [tab, setTab] = useState("appointments");
  const [profiles, setProfiles] = useState(null);
  const [appointments, setAppointments] = useState(null);
  const [open, setOpen] = useState(null);

  const authed = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        authed.get("/admin/profiles"),
        authed.get("/admin/appointments"),
      ]);
      setProfiles(p.data.profiles);
      setAppointments(a.data.appointments);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sign-in failed. Please try again.");
    }
  };

  const setStatus = async (appt_id, status) => {
    await authed.put(`/admin/appointments/${appt_id}`, { status });
    load();
  };

  const forgotSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    try {
      const res = await axios.post(`${API}/admin/forgot-password`, { email });
      setInfo(res.data.message);
      setMode("reset");
    } catch {
      setError("Something went wrong. Please try again.");
    }
  };

  const resetSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/reset-password`, { email, code, new_password: newPassword });
      setInfo(res.data.message);
      setMode("login");
      setCode("");
      setNewPassword("");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Reset failed. Please try again.");
    }
  };

  const inputCls = "w-full bg-ivory border border-line px-4 py-3.5 text-[15px] focus:outline-none focus:border-forest";

  if (!token) {
    return (
      <div className="min-h-screen bg-ivory grain relative flex items-center justify-center px-5" data-testid="admin-login-page">
        <div className="w-full max-w-md bg-white border border-line p-8 sm:p-10">
          <Logo />
          <h1 className="mt-8 font-serif text-2xl text-forest">Practice Dashboard</h1>
          <p className="mt-2 text-sm text-inkmuted">Private access for Dr Rohan Dikshit.</p>

          {mode === "login" && (
            <form onSubmit={login} className="mt-8 space-y-5">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email-input" className={inputCls} />
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="admin-password-input" className={inputCls} />
              {error && <p className="text-sm text-red-700" data-testid="admin-login-error">{error}</p>}
              {info && <p className="text-sm text-mist" data-testid="admin-info">{info}</p>}
              <button type="submit" data-testid="admin-login-btn" className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-forest text-ivory px-8 py-3.5 text-sm font-semibold tracking-wide border border-gold/40 hover:bg-forestdeep transition-colors">
                <Lock size={15} /> Sign In
              </button>
              <button type="button" onClick={() => { setMode("forgot"); setError(""); setInfo(""); }} data-testid="forgot-password-link" className="text-xs text-inkmuted hover:text-forest">
                Forgot password?
              </button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={forgotSubmit} className="mt-8 space-y-5" data-testid="forgot-form">
              <p className="text-sm text-inkmuted leading-relaxed">
                Enter your dashboard email. A 6-digit reset code will be sent to your inbox, valid for 15 minutes.
              </p>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="forgot-email-input" className={inputCls} />
              {error && <p className="text-sm text-red-700">{error}</p>}
              <button type="submit" data-testid="forgot-submit-btn" className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-forest text-ivory px-8 py-3.5 text-sm font-semibold tracking-wide border border-gold/40 hover:bg-forestdeep transition-colors">
                Send Reset Code
              </button>
              <button type="button" onClick={() => { setMode("login"); setError(""); }} className="text-xs text-inkmuted hover:text-forest">
                ← Back to sign in
              </button>
            </form>
          )}

          {mode === "reset" && (
            <form onSubmit={resetSubmit} className="mt-8 space-y-5" data-testid="reset-form">
              {info && <p className="text-sm text-mist" data-testid="reset-info">{info}</p>}
              <input type="text" required value={code} onChange={(e) => setCode(e.target.value.trim())} placeholder="6-digit code" maxLength={6} data-testid="reset-code-input" className={inputCls} />
              <input type="password" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 6 characters)" data-testid="reset-new-password-input" className={inputCls} />
              {error && <p className="text-sm text-red-700" data-testid="reset-error">{error}</p>}
              <button type="submit" data-testid="reset-submit-btn" className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-forest text-ivory px-8 py-3.5 text-sm font-semibold tracking-wide border border-gold/40 hover:bg-forestdeep transition-colors">
                Set New Password
              </button>
              <button type="button" onClick={() => { setMode("forgot"); setError(""); setInfo(""); }} className="text-xs text-inkmuted hover:text-forest">
                Resend code
              </button>
            </form>
          )}

          <Link to="/" className="mt-6 inline-block text-xs text-inkmuted hover:text-forest">← Back to website</Link>
        </div>
      </div>
    );
  }

  const pending = appointments?.filter((a) => a.status === "requested").length ?? 0;

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="border-b border-line bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Logo />
          <button
            onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); }}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 text-sm font-medium text-inkmuted hover:text-forest"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <Eyebrow>InSight Metabolics · Private</Eyebrow>
        <h1 className="mt-3 font-serif text-3xl sm:text-4xl text-forest">Practice Dashboard</h1>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-line p-6" data-testid="stat-profiles">
            <Users size={18} className="text-gold" strokeWidth={1.5} />
            <p className="mt-3 font-serif text-3xl text-forest">{profiles?.length ?? "—"}</p>
            <p className="text-xs text-inkmuted mt-1">Health profiles</p>
          </div>
          <div className="bg-white border border-line p-6" data-testid="stat-requests">
            <CalendarCheck size={18} className="text-gold" strokeWidth={1.5} />
            <p className="mt-3 font-serif text-3xl text-forest">{pending}</p>
            <p className="text-xs text-inkmuted mt-1">Requests awaiting confirmation</p>
          </div>
          <div className="bg-white border border-line p-6" data-testid="stat-appointments">
            <CalendarCheck size={18} className="text-mist" strokeWidth={1.5} />
            <p className="mt-3 font-serif text-3xl text-forest">{appointments?.length ?? "—"}</p>
            <p className="text-xs text-inkmuted mt-1">Total bookings</p>
          </div>
        </div>

        <div className="mt-10 flex gap-2 border-b border-line">
          {["appointments", "profiles"].map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-5 py-3 text-sm font-semibold tracking-wide capitalize border-b-2 -mb-px transition-colors ${
                tab === t ? "border-forest text-forest" : "border-transparent text-inkmuted hover:text-ink"
              }`}
            >
              {t === "appointments" ? "Consultation Requests" : "Health Profiles"}
            </button>
          ))}
        </div>

        {tab === "appointments" && (
          <div className="mt-6 space-y-3" data-testid="admin-appointments-list">
            {appointments?.length === 0 && <p className="text-sm text-inkmuted py-8">No consultation requests yet.</p>}
            {appointments?.map((a) => (
              <div key={a.appt_id} className="bg-white border border-line p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`appt-${a.appt_id}`}>
                <div className="flex-1">
                  <p className="font-serif text-lg text-ink">{a.full_name}</p>
                  <p className="text-xs text-inkmuted mt-0.5">{a.mobile}{a.email ? ` · ${a.email}` : ""}</p>
                </div>
                <div className="text-sm text-ink sm:text-right">
                  <p className="font-semibold">{new Date(`${a.date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</p>
                  <p className="text-xs text-inkmuted">{a.slot_label} IST</p>
                </div>
                <span className={`text-[10px] font-mono tracking-[0.14em] uppercase border px-3 py-1.5 ${STATUS_STYLES[a.status] || ""}`}>
                  {a.status}
                </span>
                <div className="flex gap-2">
                  {a.status !== "confirmed" && a.status !== "cancelled" && (
                    <button
                      data-testid={`confirm-${a.appt_id}`}
                      onClick={() => setStatus(a.appt_id, "confirmed")}
                      className="text-xs font-semibold text-forest border border-forest/30 px-4 py-2 hover:bg-forest hover:text-ivory transition-colors"
                    >
                      Confirm
                    </button>
                  )}
                  {a.status !== "cancelled" && (
                    <button
                      data-testid={`cancel-${a.appt_id}`}
                      onClick={() => setStatus(a.appt_id, "cancelled")}
                      className="text-xs font-semibold text-red-700 border border-red-200 px-4 py-2 hover:bg-red-50 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "profiles" && (
          <div className="mt-6 space-y-3" data-testid="admin-profiles-list">
            {profiles?.length === 0 && <p className="text-sm text-inkmuted py-8">No health profiles yet.</p>}
            {profiles?.map((p) => (
              <div key={p.ref_id} className="bg-white border border-line" data-testid={`profile-${p.ref_id}`}>
                <button
                  className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left"
                  onClick={() => setOpen(open === p.ref_id ? null : p.ref_id)}
                  data-testid={`profile-toggle-${p.ref_id}`}
                >
                  <div>
                    <p className="font-serif text-lg text-ink">{p.full_name} <span className="text-sm text-inkmuted">· {p.age} yrs{p.gender ? ` · ${p.gender}` : ""}</span></p>
                    <p className="text-xs text-inkmuted mt-0.5">{p.mobile}{p.email ? ` · ${p.email}` : ""} · {p.ref_id}</p>
                  </div>
                  {open === p.ref_id ? <ChevronUp size={18} className="text-inkmuted" /> : <ChevronDown size={18} className="text-inkmuted" />}
                </button>
                {open === p.ref_id && (
                  <div className="px-5 pb-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                    <Detail label="Country / Time zone" value={[p.country, p.timezone].filter(Boolean).join(" · ")} />
                    <Detail label="Submitted" value={new Date(p.created_at).toLocaleString("en-GB")} />
                    <Detail label="What brings them" value={p.concerns} />
                    <Detail label="Top 3 goals" value={p.goals} />
                    <Detail label="Currently experiencing" value={p.experiencing} />
                    <Detail label="Sleep / Stress" value={[p.sleep_hours, p.stress_level && `Stress: ${p.stress_level}`].filter(Boolean).join(" · ")} />
                    <Detail label="Exercise routine" value={p.exercise} />
                    <Detail label="Already tried" value={p.tried} />
                    <Detail label="What made it difficult" value={p.obstacles} />
                    <Detail label="One thing in 6 months" value={p.six_month_goal} />
                    <Detail label="Anything else" value={p.additional_notes} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
